import { useCallback, useEffect, useMemo, useState } from "react";
import { formatDateTime } from "../../lib/format";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Input,
  PageHeader,
  Select,
  SkeletonRows,
  Table,
  Tabs,
  useToast,
} from "../../components/ui";
import type { BadgeTone, Column } from "../../components/ui";
import {
  observabilityService,
  type ErrorEventItem,
  type LogEntry,
  type LogFilters,
  type LogMeta,
} from "../../services/observability.service";

const PERIODS = [
  { value: "1h", label: "Última hora" },
  { value: "24h", label: "Últimas 24 horas" },
  { value: "7d", label: "Últimos 7 dias" },
  { value: "30d", label: "Últimos 30 dias" },
  { value: "", label: "Todo o período" },
];

function periodStart(period: string): string | undefined {
  if (!period) return undefined;
  const now = Date.now();
  const ms: Record<string, number> = {
    "1h": 3_600_000,
    "24h": 86_400_000,
    "7d": 604_800_000,
    "30d": 2_592_000_000,
  };
  return new Date(now - (ms[period] ?? 0)).toISOString();
}

function levelTone(level: string): BadgeTone {
  switch (level.toLowerCase()) {
    case "error":
    case "critical":
    case "emergency":
    case "alert":
      return "danger";
    case "warning":
      return "warn";
    case "info":
    case "notice":
      return "info";
    default:
      return "neutral";
  }
}


export default function ObservabilityPage() {
  const toast = useToast();
  const [tab, setTab] = useState("logs");

  const [meta, setMeta] = useState<LogMeta | null>(null);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [errors, setErrors] = useState<ErrorEventItem[]>([]);
  const [loading, setLoading] = useState(true);

  const [level, setLevel] = useState("");
  const [source, setSource] = useState("");
  const [project, setProject] = useState("");
  const [period, setPeriod] = useState("24h");
  const [search, setSearch] = useState("");

  const filters = useMemo<LogFilters>(
    () => ({
      level: level || undefined,
      source: source || undefined,
      project: project.trim() || undefined,
      from: periodStart(period),
      search: search.trim() || undefined,
      limit: 200,
    }),
    [level, source, project, period, search],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === "logs") {
        const response = await observabilityService.logs(filters);
        setLogs(response.data);
        setMeta(response.meta);
      } else {
        setErrors(
          await observabilityService.errors({
            level: level || undefined,
            source: source || undefined,
            project: project.trim() || undefined,
            from: periodStart(period),
            limit: 100,
          }),
        );
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Não foi possível carregar a operação.");
    } finally {
      setLoading(false);
    }
  }, [tab, filters, level, source, project, period, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const sourceOptions = useMemo(() => {
    const builder = meta?.builder_sources ?? ["backend", "worker", "runner", "ai", "deploy"];
    const project = meta?.project_sources ?? ["frontend", "backend", "build", "database"];
    return [
      { value: "", label: "Todas as origens" },
      ...Array.from(new Set([...builder, ...project])).map((item) => ({ value: item, label: item })),
    ];
  }, [meta]);

  const logColumns: Column<LogEntry>[] = [
    {
      key: "logged_at",
      header: "Quando",
      render: (row) => <span className="font-mono text-xs">{formatDateTime(row.logged_at)}</span>,
    },
    {
      key: "level",
      header: "Nível",
      render: (row) => <Badge tone={levelTone(row.level)}>{row.level}</Badge>,
    },
    {
      key: "source",
      header: "Origem",
      hideOnMobile: true,
      render: (row) => (
        <span className="font-mono text-xs text-ink-muted">
          {row.scope === "project" ? "projeto" : "builder"} / {row.source}
        </span>
      ),
    },
    {
      key: "project",
      header: "Projeto",
      hideOnMobile: true,
      render: (row) => <span className="text-xs">{row.project_name ?? "—"}</span>,
    },
    {
      key: "message",
      header: "Mensagem",
      render: (row) => <span className="block max-w-2xl break-words text-xs">{row.message}</span>,
    },
  ];

  const errorColumns: Column<ErrorEventItem>[] = [
    {
      key: "type",
      header: "Erro",
      render: (row) => (
        <div>
          <p className="font-medium text-ink">{row.type}</p>
          <p className="mt-1 max-w-xl break-words text-xs text-ink-muted">{row.message}</p>
        </div>
      ),
    },
    {
      key: "source",
      header: "Origem",
      hideOnMobile: true,
      render: (row) => <Badge tone={levelTone(row.level)}>{row.source}</Badge>,
    },
    {
      key: "project",
      header: "Projeto",
      hideOnMobile: true,
      render: (row) => <span className="text-xs">{row.project?.name ?? "—"}</span>,
    },
    {
      key: "occurrences",
      header: "Ocorrências",
      align: "right",
      render: (row) => <span className="font-mono text-xs">{row.occurrences}</span>,
    },
    {
      key: "seen",
      header: "Primeiro / último",
      hideOnMobile: true,
      render: (row) => (
        <span className="font-mono text-[11px] text-ink-muted">
          {formatDateTime(row.first_seen_at)}
          <br />
          {formatDateTime(row.last_seen_at)}
        </span>
      ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (row) => (
        <Button
          variant="ghost"
          onClick={async () => {
            try {
              const result = await observabilityService.toggleError(row.id);
              setErrors((current) =>
                current.map((item) => (item.id === row.id ? { ...item, resolved: result.resolved } : item)),
              );
            } catch (error) {
              toast.error(error instanceof Error ? error.message : "Não foi possível atualizar o erro.");
            }
          }}
        >
          {row.resolved ? "Reabrir" : "Resolver"}
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Operação"
        description="Logs do Builder e dos projetos, erros agrupados por assinatura e histórico de ocorrências. Mensagens são sanitizadas: nenhum segredo é armazenado."
        actions={
          <Button variant="secondary" onClick={() => void load()}>
            Atualizar
          </Button>
        }
      />

      <Card>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <Select
            label="Nível"
            value={level}
            onChange={(event) => setLevel(event.target.value)}
            options={[
              { value: "", label: "Todos" },
              ...(meta?.levels ?? ["debug", "info", "notice", "warning", "error", "critical"]).map((item) => ({
                value: item,
                label: item,
              })),
            ]}
          />
          <Select
            label="Origem"
            value={source}
            onChange={(event) => setSource(event.target.value)}
            options={sourceOptions}
          />
          <Select
            label="Período"
            value={period}
            onChange={(event) => setPeriod(event.target.value)}
            options={PERIODS}
          />
          <Input
            label="Projeto (ID)"
            value={project}
            onChange={(event) => setProject(event.target.value)}
            placeholder="opcional"
          />
          <Input
            label="Busca"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="texto na mensagem"
          />
        </div>
      </Card>

      <Tabs
        items={[
          { id: "logs", label: "Logs", content: null },
          { id: "errors", label: "Erros", content: null },
        ]}
        value={tab}
        onChange={setTab}
      />

      <Card>
        {loading ? (
          <SkeletonRows rows={6} />
        ) : tab === "logs" ? (
          <Table
            columns={logColumns}
            rows={logs}
            rowKey={(row) => row.id}
            empty={<EmptyState title="Sem registros" description="Nenhum log no período e filtros escolhidos." />}
          />
        ) : (
          <Table
            columns={errorColumns}
            rows={errors}
            rowKey={(row) => row.id}
            empty={<EmptyState title="Nenhum erro" description="Nenhum erro agrupado para os filtros atuais." />}
          />
        )}
      </Card>
    </div>
  );
}
